/**
 * The step types an operation type allows — chosen from the same closed vocabulary, laid out
 * the same way, as the "What happens here?" toolbox that adds a step. Here a tile is a toggle:
 * what is chosen is what the record designer's picker will offer on an operation built from
 * this type, so an author sees the choice in the shape they will later pick from.
 *
 * Wrapped in `.dz` so the tokens resolve inside a modal, which antd portals outside the page.
 */
import React, { useMemo } from "react";
import { Button, Typography } from "antd";

const { Text } = Typography;

export default function StepTypePicker({ vocab, value = [], onChange, disabled = false }) {
  const groups = useMemo(() => {
    const out = new Map();
    (vocab?.step_types || []).forEach((t) => {
      const family = t.group || "All";
      if (!out.has(family)) out.set(family, []);
      out.get(family).push(t);
    });
    return [...out.entries()];
  }, [vocab]);
  const chosen = new Set(value);
  const toggle = (v) => onChange?.(chosen.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  const all = (vocab?.step_types || []).map((t) => t.value);

  return (
    <div className="dz u0-steptypes" style={{ background: "transparent" }}>
      <div className="u0-steptypes-head">
        <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
          {value.length ? `${value.length} of ${all.length} chosen` : "None chosen — the record offers the step types this type's own steps use"}
        </Text>
        {!disabled ? (
          <span>
            <Button size="small" type="link" onClick={() => onChange?.(all)}>All</Button>
            <Button size="small" type="link" onClick={() => onChange?.([])} disabled={!value.length}>Clear</Button>
          </span>
        ) : null}
      </div>
      {groups.map(([family, items]) => (
        <div key={family} className="u0-toolgrp">
          <div className="u0-eyebrow">{family}</div>
          <div className="u0-tools">
            {items.map((t) => (
              <button key={t.value} type="button" disabled={disabled}
                      className={`u0-tool ${chosen.has(t.value) ? "on" : ""}`}
                      aria-pressed={chosen.has(t.value)} title={t.hint || t.label}
                      onClick={() => toggle(t.value)}>
                {t.code ? <code>{t.code}</code> : null}
                <span className="n">{t.label}</span>
                {t.hint ? <span className="h">{t.hint}</span> : null}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
